import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, LogOut } from "lucide-react";
import { api, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AIStudio } from "@/components/AIStudio";
import { AdminRecovery } from "@/components/AdminRecovery";
import useSEO from "@/lib/useSEO";

// Plain-English fulfillment states for the orders table — no internal jargon.
const FULFILL_INFO = {
  address_hold: ["NEEDS ADDRESS", "text-red-700 font-bold"],
  needs_attention: ["NEEDS ATTENTION", "text-red-700 font-bold"],
  awaiting_submission: ["READY — SEND TO PRINTER", "text-primary font-bold"],
  on_hold: ["WAITING YOUR APPROVAL", "text-primary font-bold"],
  in_production: ["PRINTING", "text-black"],
  sent_to_production: ["PRINTING", "text-black"],
  submitted: ["SENDING TO PRINTER", "text-black/60"],
  submitting: ["SENDING TO PRINTER", "text-black/60"],
  shipped: ["SHIPPED", "text-green-700 font-bold"],
  delivered: ["DELIVERED", "text-green-700 font-bold"],
  failed: ["FAILED — NEEDS ATTENTION", "text-red-700 font-bold"],
  submission_uncertain: ["SUBMIT UNCERTAIN — RECONCILE", "text-red-700 font-bold"],
  none: ["—", "text-black/40"],
};
const ACTIONABLE_FULFILL = new Set(["address_hold", "needs_attention", "awaiting_submission", "on_hold", "failed", "submission_uncertain"]);
const ACTIVE_FULFILL = new Set(["in_production", "sent_to_production", "submitted", "submitting", "shipped", "delivered"]);
// Test/probe orders never count as real orders awaiting the owner's review
const TEST_EMAIL_RE = /(@example\.com$)|(@test\.)|(^preflight@)|(^qa\.)/i;
export const isAwaitingReview = (o) =>
  o.payment_status === "paid" &&
  o.status !== "cancelled" &&
  o.fulfillment_status === "awaiting_submission" &&
  !TEST_EMAIL_RE.test((o.customer || {}).email || "");
// Total the customer actually paid (Stripe amount incl. tax); falls back to total + tax
export const totalPaid = (o) =>
  o.payment_status === "paid" ? (o.amount_paid ?? (o.total + (o.tax || 0))) : o.total;
const orderRowWeight = (o) =>
  isAwaitingReview(o) ? -1
    : o.status === "cancelled" ? 3
    : ACTIONABLE_FULFILL.has(o.fulfillment_status) ? 0
    : ACTIVE_FULFILL.has(o.fulfillment_status) ? 1
    : 2;
export const sortOrders = (list) =>
  [...list].sort((a, b) =>
    orderRowWeight(a) - orderRowWeight(b) ||
    String(b.created_at || "").localeCompare(String(a.created_at || ""))
  );

const EMPTY_FORM = {
  name: "",
  slogan: "",
  description: "",
  type: "tee",
  color: "Black",
  colors: "Black,White,Grey",
  price: "",
  image: "",
  sizes: "S,M,L,XL,XXL",
  featured: false,
};

function ProductForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      set("image", data.url);
      toast.success("Photo uploaded and attached.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  return (
    <form
      data-testid="product-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="space-y-4"
    >
      {[
        { k: "name", label: "Name", placeholder: "The Empathy Tee" },
        { k: "slogan", label: "Slogan", placeholder: "MAKE EMPATHY GREAT AGAIN" },
      ].map((f) => (
        <div key={f.k}>
          <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">{f.label}</label>
          <input
            required={f.k !== "image"}
            value={form[f.k]}
            placeholder={f.placeholder}
            onChange={(e) => set(f.k, e.target.value)}
            data-testid={`product-form-${f.k}`}
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm outline-none focus:shadow-hard transition-shadow"
          />
        </div>
      ))}
      <div>
        <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">Photo — upload or paste path</label>
        <div className="flex gap-2">
          <input
            value={form.image}
            placeholder="/api/static/products/xxx.webp"
            onChange={(e) => set("image", e.target.value)}
            data-testid="product-form-image"
            className="flex-1 border-2 border-black px-3 py-2 font-mono text-sm outline-none focus:shadow-hard transition-shadow"
          />
          <label
            data-testid="product-form-upload"
            className="cursor-pointer border-2 border-black bg-accent px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider shadow-hard btn-press whitespace-nowrap"
          >
            {uploading ? "Uploading…" : "Upload"}
            <input type="file" accept="image/*" onChange={uploadFile} className="hidden" />
          </label>
        </div>
        {form.image && (
          <img src={imgUrl(form.image)} alt="preview" className="mt-2 h-16 w-14 border-2 border-black object-cover" data-testid="product-form-image-preview" />
        )}
      </div>
      <div>
        <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          rows={3}
          data-testid="product-form-description"
          className="w-full border-2 border-black px-3 py-2 font-mono text-sm outline-none focus:shadow-hard transition-shadow"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">Type</label>
          <select
            value={form.type}
            onChange={(e) => set("type", e.target.value)}
            data-testid="product-form-type"
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm bg-background"
          >
            <option value="tee">Tee</option>
            <option value="hoodie">Hoodie</option>
          </select>
        </div>
        <div>
          <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">Color</label>
          <select
            value={form.color}
            onChange={(e) => set("color", e.target.value)}
            data-testid="product-form-color"
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm bg-background"
          >
            <option>Black</option>
            <option>Natural</option>
            <option>Ash</option>
          </select>
        </div>
        <div>
          <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">Price (USD)</label>
          <input
            required
            type="number"
            min="1"
            step="0.01"
            value={form.price}
            onChange={(e) => set("price", e.target.value)}
            data-testid="product-form-price"
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm outline-none"
          />
        </div>
        <div>
          <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">Sizes (comma-sep)</label>
          <input
            value={form.sizes}
            onChange={(e) => set("sizes", e.target.value)}
            data-testid="product-form-sizes"
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm outline-none"
          />
        </div>
        <div className="col-span-2">
          <label className="block mb-1 font-mono text-xs font-bold uppercase tracking-[0.2em]">Colors (comma-sep)</label>
          <input
            value={form.colors}
            onChange={(e) => set("colors", e.target.value)}
            data-testid="product-form-colors"
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm outline-none"
          />
        </div>
      </div>
      <label className="flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.2em]">
        <input
          type="checkbox"
          checked={form.featured}
          onChange={(e) => set("featured", e.target.checked)}
          data-testid="product-form-featured"
          className="h-4 w-4 accent-[#FF4D00]"
        />
        Featured on homepage
      </label>
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          data-testid="product-form-save"
          className="flex-1 border-2 border-black bg-black py-3 font-display uppercase text-lg text-background shadow-hard btn-press hover:bg-primary disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save product"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="product-form-cancel"
          className="border-2 border-black px-6 font-display uppercase text-lg shadow-hard btn-press hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  useSEO("Admin Dashboard | Fleece The Regime", "Store administration.", true);
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("orders");
  const [showAwaitingOnly, setShowAwaitingOnly] = useState(false);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadReviews = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  const loadProfits = () => api.get("/admin/profit").then((r) => setProfits(r.data)).catch(() => {});
  const [profits, setProfits] = useState(null);
  const [mapping, setMapping] = useState(null);
  const loadMapping = () => api.get("/admin/printify/mapping").then((r) => setMapping(r.data)).catch(() => {});
  const [fulfillMode, setFulfillMode] = useState("manual");
  const loadFulfillMode = () => api.get("/admin/printify/settings/fulfillment-mode").then((r) => setFulfillMode(r.data.mode)).catch(() => {});
  const [attentionCount, setAttentionCount] = useState(0);
  const loadAttentionCount = () => api.get("/admin/recovery/queue").then((r) => setAttentionCount(r.data.count)).catch(() => {});
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const loadAnalytics = (d = analyticsDays) =>
    api.get(`/admin/analytics/summary?days=${d}`).then((r) => setAnalytics(r.data)).catch(() => {});

  const changeFulfillMode = async (m) => {
    if (m === "auto_full" && !window.confirm("FULL AUTO: every paid order goes straight to production and charges your Printify balance. Enable?")) return;
    try {
      await api.post("/admin/printify/settings/fulfillment-mode", { mode: m });
      setFulfillMode(m);
      toast.success(`Fulfillment mode: ${m === "manual" ? "manual approval" : m === "auto_submit" ? "auto-submit (you approve production)" : "FULL AUTO"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const approveMapping = async (slug) => {
    try {
      const { data } = await api.post("/admin/printify/mapping/approve-product", { slug });
      toast.success(`Approved ${data.approved} variants for ${slug}.`);
      loadMapping();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const printifySubmit = async (order) => {
    try {
      const { data } = await api.post(`/admin/printify/orders/${order.id}/submit`);
      toast.success(`On hold at Printify (${String(data.printify_order_id).slice(-8)}). Nothing in production.`);
      loadOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const printifyApprove = async (order) => {
    if (!window.confirm(`Send ${order.order_number} to PRODUCTION at Printify? This charges your Printify balance.`)) return;
    try {
      await api.post(`/admin/printify/orders/${order.id}/approve`);
      toast.success("Sent to production.");
      loadOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const printifyCosts = async (order) => {
    try {
      const { data } = await api.get(`/admin/printify/orders/${order.id}/costs`);
      toast.success(`Printify cost: $${data.total_fulfillment.toFixed(2)} print + $${data.total_shipping.toFixed(2)} ship (status ${data.printify_status})`);
      loadOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Delete ${order.order_number}? Unpaid orders only — this cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Deleted ${order.order_number}.`);
      loadOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resendReceipt = async (order) => {
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/resend-confirmation`);
      toast.success(`Receipt resent to ${data.to} — itemized with tax and total paid.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => {
        setChecking(false);
        loadProducts();
        loadOrders();
        loadProfits();
        loadReviews();
        loadMapping();
        loadFulfillMode();
        loadAttentionCount();
        loadAnalytics(30);
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  // Keep the awaiting-review count fresh without a manual reload
  useEffect(() => {
    const t = setInterval(loadOrders, 30000);
    const onFocus = () => loadOrders();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  const awaitingOrders = sortOrders(orders.filter(isAwaitingReview));
  const awaitingCount = awaitingOrders.length;

  const saveProduct = async (form) => {
    setSaving(true);
    const payload = {
      name: form.name,
      slogan: form.slogan,
      description: form.description,
      type: form.type,
      color: form.color,
      price: parseFloat(form.price),
      image: form.image || `/api/static/products/${form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      featured: form.featured,
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated." : "Product added to the resistance.");
      setDialogOpen(false);
      setEditing(null);
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Deleted. It knew the risks.");
      loadProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updateOrderStatus = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status });
      loadOrders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (checking) return <main className="pt-32 px-6 font-mono text-sm text-muted-foreground">Checking credentials…</main>;

  return (
    <main data-testid="admin-dashboard" className="pt-16 min-h-screen">
      <div className="px-6 sm:px-12 py-12 border-b-2 border-black flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">Back office</p>
          <h1 className="font-display uppercase text-5xl sm:text-6xl tracking-tighter leading-[0.85]">The Ministry<br />of Merch</h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-add-product"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="inline-flex items-center gap-2 border-2 border-black bg-primary px-5 py-3 font-display uppercase text-lg text-white shadow-hard btn-press hover:bg-black"
          >
            <Plus size={18} /> Add product
          </button>
          <button
            data-testid="admin-logout"
            onClick={() => {
              localStorage.removeItem("ut_admin_token");
              navigate("/admin/login");
            }}
            className="inline-flex items-center gap-2 border-2 border-black px-5 py-3 font-display uppercase text-lg shadow-hard btn-press hover:bg-accent"
          >
            <LogOut size={18} /> Out
          </button>
        </div>
      </div>

      <div className="flex overflow-x-auto border-b-2 border-black font-mono text-xs font-bold uppercase tracking-[0.15em]">
        {["products", "orders", "fulfillment", "profits", "reviews", "analytics", "help", "ai studio"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`shrink-0 whitespace-nowrap px-6 py-4 border-r-2 border-black transition-colors ${tab === t ? "bg-black text-background" : "hover:bg-accent"}`}
          >
            {t} {t === "orders" && `(${orders.length})`} {t === "reviews" && `(${reviews.length})`}
            {t === "orders" && awaitingCount > 0 && (
              <span data-testid="orders-awaiting-badge" className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 border-2 border-black bg-primary text-white font-mono text-[10px] font-bold">
                {awaitingCount} new
              </span>
            )}
            {t === "help" && attentionCount > 0 && (
              <span data-testid="help-attention-badge" className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 border-2 border-black bg-primary text-white font-mono text-[10px] font-bold">
                {attentionCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="px-6 sm:px-12 py-12 overflow-x-auto">
        {tab === "ai studio" && <AIStudio />}
        {tab === "help" && <AdminRecovery />}
        {tab === "products" && (
          <>
          <div className="mb-4 flex items-center justify-between border-2 border-black bg-background p-3">
            <p className="font-mono text-xs text-black/60 uppercase tracking-[0.15em]">
              Stripe prices mirror the locked catalog. After adding products, sync once so live checkout can charge them.
            </p>
            <button
              data-testid="admin-sync-prices"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/stripe/sync-prices");
                  toast.success(`Price sync complete: ${data.synced} products ensured (${data.key_mode} mode). Safe to re-run.`);
                } catch (e) {
                  toast.error("Price sync failed — check the backend logs");
                }
              }}
              className="ml-4 whitespace-nowrap border-2 border-black bg-accent px-4 py-2 font-mono text-xs font-bold uppercase shadow-hard btn-press"
            >
              Sync Stripe prices
            </button>
          </div>
          <Table data-testid="admin-products-table">
            <TableHeader>
              <TableRow className="border-b-2 border-black">
                <TableHead className="font-mono text-xs uppercase">Img</TableHead>
                <TableHead className="font-mono text-xs uppercase">Name</TableHead>
                <TableHead className="font-mono text-xs uppercase">Slogan</TableHead>
                <TableHead className="font-mono text-xs uppercase">Type</TableHead>
                <TableHead className="font-mono text-xs uppercase">Price</TableHead>
                <TableHead className="font-mono text-xs uppercase">Featured</TableHead>
                <TableHead className="font-mono text-xs uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} data-testid={`admin-product-row-${p.slug}`} className="border-b border-black/20">
                  <TableCell>
                    <img src={imgUrl(p.image)} alt="" className="h-12 w-10 object-cover border border-black bg-black" />
                  </TableCell>
                  <TableCell className="font-display uppercase">{p.name}</TableCell>
                  <TableCell className="font-mono text-xs text-primary font-bold">{p.slogan}</TableCell>
                  <TableCell className="font-mono text-xs uppercase">{p.type}</TableCell>
                  <TableCell className="font-mono text-sm font-bold">${p.price.toFixed(0)}</TableCell>
                  <TableCell className="font-mono text-xs">{p.featured ? "★" : "—"}</TableCell>
                  <TableCell className="text-right">
                    <button
                      data-testid={`edit-${p.slug}`}
                      onClick={() => {
                        setEditing(p);
                        setDialogOpen(true);
                      }}
                      className="mr-2 inline-flex h-8 w-8 items-center justify-center border-2 border-black hover:bg-accent transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`delete-${p.slug}`}
                      onClick={() => deleteProduct(p)}
                      className="inline-flex h-8 w-8 items-center justify-center border-2 border-black hover:bg-primary hover:text-white transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
        )}

        {tab === "orders" && (
          <>
            <div
              data-testid="awaiting-review-banner"
              className={`mb-6 flex flex-wrap items-center justify-between gap-4 border-2 border-black p-5 ${awaitingCount > 0 ? "bg-primary text-white shadow-hard" : "bg-accent/40"}`}
            >
              <p className="font-display uppercase text-2xl tracking-tight" data-testid="awaiting-review-count">
                New orders awaiting review: {awaitingCount}
              </p>
              <button
                data-testid="review-new-orders-btn"
                onClick={() => setShowAwaitingOnly((v) => !v)}
                disabled={awaitingCount === 0 && !showAwaitingOnly}
                className={`border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase transition-colors ${awaitingCount > 0 ? "bg-black text-background hover:bg-background hover:text-black" : "bg-background hover:bg-accent"} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {showAwaitingOnly ? "Show all orders" : "Review new orders"}
              </button>
            </div>
            {orders.length === 0 && (
              <p className="font-mono text-sm text-muted-foreground" data-testid="admin-orders-empty">
                No orders yet. The people are still deciding.
              </p>
            )}
            {orders.length > 0 && showAwaitingOnly && awaitingCount === 0 && (
              <p className="font-mono text-sm text-muted-foreground" data-testid="admin-orders-none-awaiting">
                Nothing waiting on you — every paid order is already with the printer.
              </p>
            )}
            {orders.length > 0 && (!showAwaitingOnly || awaitingCount > 0) && (
              <Table data-testid="admin-orders-table">
                <TableHeader>
                  <TableRow className="border-b-2 border-black">
                    <TableHead className="font-mono text-xs uppercase">Order</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Date</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Customer</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Items</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Total paid</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Payment</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Fulfillment</TableHead>
                    <TableHead className="font-mono text-xs uppercase"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(showAwaitingOnly ? awaitingOrders : sortOrders(orders)).map((o) => (
                    <TableRow key={o.id} data-testid={`admin-order-row-${o.order_number}`} className={`border-b border-black/20 ${o.status === "cancelled" ? "opacity-45" : ""} ${isAwaitingReview(o) ? "bg-primary/10" : ""}`}>
                      <TableCell className="font-mono text-sm font-bold">
                        {o.order_number}
                        {isAwaitingReview(o) && (
                          <span data-testid={`awaiting-flag-${o.order_number}`} className="ml-2 border border-primary bg-primary px-1 py-0.5 font-mono text-[9px] font-bold uppercase text-white">awaiting review</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {o.created_at ? new Date(o.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {(o.customer || {}).name || "— collected at payment —"}
                        <br />
                        <span className="text-black/50">{(o.customer || {}).email || ""}</span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {o.items.map((i) => `${i.name} (${i.size}×${i.qty})`).join(", ")}
                      </TableCell>
                      <TableCell className="font-mono text-sm font-bold" data-testid={`order-total-${o.order_number}`}>
                        ${totalPaid(o).toFixed(2)}
                        {(o.tax || 0) > 0 && (
                          <span className="block text-[10px] font-normal text-black/50">incl. tax ${Number(o.tax).toFixed(2)}</span>
                        )}
                        {o.payment_status !== "paid" && (
                          <span className="block text-[10px] font-normal text-black/50">unpaid</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs uppercase">{o.payment_status || "pending"}</TableCell>
                      <TableCell>
                        <select
                          value={o.status}
                          onChange={(e) => updateOrderStatus(o, e.target.value)}
                          data-testid={`order-status-${o.order_number}`}
                          className="border-2 border-black px-2 py-1 font-mono text-xs bg-background"
                        >
                          <option value="placed">placed</option>
                          <option value="shipped">shipped</option>
                          <option value="cancelled">cancelled</option>
                        </select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          {o.needs_address && (
                            <span className="border-2 border-red-600 bg-red-600 text-white px-2 py-1 font-mono text-[10px] font-bold uppercase" data-testid={`address-missing-${o.order_number}`}>
                              Address missing: {(o.address_missing || []).join(", ")}
                            </span>
                          )}
                          <span className={`font-mono text-xs uppercase ${(FULFILL_INFO[o.fulfillment_status] || FULFILL_INFO.none)[1]}`} data-testid={`fulfillment-status-${o.order_number}`}>
                            {o.status === "cancelled" ? "CANCELLED" : (FULFILL_INFO[o.fulfillment_status] || FULFILL_INFO.none)[0]}
                          </span>
                          {["partial", "full"].includes(o.refund_status) && (
                            <span className="border-2 border-black bg-black text-white px-2 py-1 font-mono text-[10px] font-bold uppercase" data-testid={`refund-badge-${o.order_number}`}>
                              {o.refund_status === "full" ? "Refunded" : "Partial refund"} ${(o.refunded_total || 0).toFixed(2)}
                            </span>
                          )}
                          {o.status !== "cancelled" && o.fulfillment_status === "awaiting_submission" && (
                            <button
                              data-testid={`printify-submit-${o.order_number}`}
                              onClick={() => printifySubmit(o)}
                              className="border-2 border-black bg-primary text-white px-2 py-1 font-mono text-[10px] font-bold uppercase hover:bg-black transition-colors"
                            >
                              Submit to Printify (hold)
                            </button>
                          )}
                          {o.status !== "cancelled" && o.fulfillment_status === "on_hold" && (
                            <>
                              <button
                                data-testid={`printify-approve-${o.order_number}`}
                                onClick={() => printifyApprove(o)}
                                className="border-2 border-black bg-black text-background px-2 py-1 font-mono text-[10px] font-bold uppercase hover:bg-primary transition-colors"
                              >
                                Approve → production
                              </button>
                              <button
                                data-testid={`printify-costs-${o.order_number}`}
                                onClick={() => printifyCosts(o)}
                                className="border-2 border-black px-2 py-1 font-mono text-[10px] font-bold uppercase hover:bg-accent transition-colors"
                              >
                                Costs
                              </button>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2 items-start">
                          {o.payment_status === "paid" && (
                            <button
                              data-testid={`order-resend-receipt-${o.order_number}`}
                              onClick={() => resendReceipt(o)}
                              className="border-2 border-black px-2 py-1 font-mono text-[10px] font-bold uppercase hover:bg-accent transition-colors"
                            >
                              Resend receipt
                            </button>
                          )}
                          {o.payment_status !== "paid" && (
                            <button
                              data-testid={`order-delete-${o.order_number}`}
                              onClick={() => deleteOrder(o)}
                              className="border-2 border-black px-2 py-1 font-mono text-[10px] font-bold uppercase hover:bg-primary hover:text-white transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}

        {tab === "fulfillment" && (
          <div data-testid="fulfillment-tab" className="space-y-6">
            <div className="border-2 border-black bg-accent/30 p-5 font-mono text-sm space-y-2">
              <p className="font-bold uppercase" data-testid="fulfillment-mode-label">
                Printify — {fulfillMode === "manual" ? "manual approval mode" : fulfillMode === "auto_submit" ? "auto-submit mode" : "FULL AUTO mode"}
              </p>
              <p>
                {fulfillMode === "manual" && <>Paid orders queue as <b>awaiting_submission</b>. Nothing prints until you click "Submit to Printify" (order sits ON HOLD) and then "Approve → production".</>}
                {fulfillMode === "auto_submit" && <>Paid orders automatically create an <b>on-hold</b> Printify order. Nothing prints until you click "Approve → production".</>}
                {fulfillMode === "auto_full" && <><b>Paid orders go straight to production</b> and charge your Printify balance automatically. Switch back to manual anytime.</>}
                {" "}Storefront, images and prices are untouched.
              </p>
              <div className="flex flex-wrap gap-2 pt-1" data-testid="fulfillment-mode-switch">
                {[["manual", "Manual"], ["auto_submit", "Auto-submit"], ["auto_full", "Full auto"]].map(([m, label]) => (
                  <button
                    key={m}
                    data-testid={`fulfill-mode-${m}`}
                    onClick={() => changeFulfillMode(m)}
                    className={`border-2 border-black px-3 py-1 font-mono text-[11px] font-bold uppercase transition-colors ${fulfillMode === m ? "bg-primary text-white" : "bg-background hover:bg-accent"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-primary font-bold">Note: Lane Seven LS19001 has no size XS at any Printify provider — hoodie XS is unmapped and flagged below.</p>
              <p className="mt-2 font-mono text-xs text-muted-foreground" data-testid="size-chart-reminder">Size charts on product pages are manual (manufacturer spec sheets for CC1717 / LS19001 / BC1010). If you ever switch a blank here, tell the builder to update the size chart too.</p>
            </div>
            {!mapping && <p className="font-mono text-sm text-muted-foreground">Loading mapping…</p>}
            {mapping && (
              <Table data-testid="printify-mapping-table">
                <TableHeader>
                  <TableRow className="border-b-2 border-black">
                    <TableHead className="font-mono text-xs uppercase">Product</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Printify blank</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Provider</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Variants</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mapping.by_product.map((m, mi) => (
                    <TableRow key={`${m.slug}-${mi}`} data-testid={`mapping-row-${m.slug}-${mi}`} className="border-b border-black/20">
                      <TableCell className="font-mono text-xs font-bold">{m.slug}</TableCell>
                      <TableCell className="font-mono text-xs">{m.blank}</TableCell>
                      <TableCell className="font-mono text-xs">{m.provider}</TableCell>
                      <TableCell className="font-mono text-xs">{m.variants}</TableCell>
                      <TableCell>
                        <span className={`font-mono text-xs uppercase font-bold ${m.status === "approved" ? "text-green-700" : "text-primary"}`}
                          data-testid={`mapping-status-${m.slug}`}>
                          {m.status === "approved" ? "approved" : "pending review"}
                        </span>
                        {m.status !== "approved" && (
                          <button
                            data-testid={`mapping-approve-${m.slug}`}
                            onClick={() => approveMapping(m.slug)}
                            className="ml-3 border-2 border-black px-2 py-1 font-mono text-[10px] font-bold uppercase hover:bg-accent transition-colors"
                          >
                            Approve blank
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {tab === "profits" && (
          <>
            {!profits && (
              <p className="font-mono text-sm text-muted-foreground" data-testid="profits-loading">Crunching numbers…</p>
            )}
            {profits && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mb-10" data-testid="profits-totals">
                  {[
                    ["Revenue", profits.totals.revenue],
                    ["Sales tax (pass-through)", profits.totals.sales_tax ?? 0],
                    ["Print costs", profits.totals.cost],
                    ["Shipping", profits.totals.shipping],
                    ["Printify tax", profits.totals.fulfill_tax ?? 0],
                    ["Stripe fees", profits.totals.fees],
                    ["Profit", profits.totals.profit],
                    ["Charity pledge", profits.totals.charity_pledge ?? 0],
                  ].map(([label, val]) => (
                    <div key={label} className={`border-2 border-black p-4 shadow-hard ${label === "Profit" ? "bg-accent" : label === "Charity pledge" ? "bg-primary text-white" : "bg-background"}`}>
                      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black/60">{label}</p>
                      <p className="mt-2 font-display text-3xl" data-testid={`profits-total-${label.toLowerCase().replace(/\s/g, "-")}`}>
                        ${val.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
                {profits.unit_economics && (
                  <div className="mb-10" data-testid="unit-economics">
                    <h3 className="font-display text-2xl uppercase mb-4">Per-item economics</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {profits.unit_economics.map((u) => (
                        <div key={`${u.type}-${u.size}`} className="border-2 border-black p-4 shadow-hard bg-background" data-testid={`unit-econ-${u.type}-${u.size}`}>
                          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black/60">
                            {u.type === "baby-tee" ? "Baby tee" : u.type === "tee" ? "Tee" : "Hoodie"} · size {u.size} · single-item order
                          </p>
                          <p className="mt-2 font-display text-3xl">${u.profit.toFixed(2)}</p>
                          <p className="font-mono text-xs mt-1">{u.margin}% margin on ${u.charged.toFixed(2)} charged</p>
                          <div className="mt-3 font-mono text-xs space-y-1 text-black/70">
                            <p>${u.price.toFixed(2)} item + ${(u.charged - u.price).toFixed(2)} shipping</p>
                            <p>−${u.cost.toFixed(2)} print · −${u.printify_shipping.toFixed(2)} ship · −${u.fulfill_tax.toFixed(2)} printify tax</p>
                            <p>−${u.stripe_fee.toFixed(2)} stripe+climate · −${u.packing_insert.toFixed(2)} insert</p>
                          </div>
                        </div>
                      ))}
                      {profits.mixed_example && (
                        <div className="border-2 border-black p-4 shadow-hard bg-accent" data-testid="unit-econ-mixed">
                          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black/60">
                            Tee + hoodie + baby · mixed order
                          </p>
                          <p className="mt-2 font-display text-3xl">${profits.mixed_example.profit.toFixed(2)}</p>
                          <p className="font-mono text-xs mt-1">{profits.mixed_example.margin}% margin on ${profits.mixed_example.revenue.toFixed(2)} charged</p>
                          <div className="mt-3 font-mono text-xs space-y-1 text-black/70">
                            <p>${profits.mixed_example.subtotal.toFixed(2)} items + ${profits.mixed_example.shipping_charged.toFixed(2)} shipping</p>
                            <p>−${profits.mixed_example.cost.toFixed(2)} print · −${profits.mixed_example.printify_shipping.toFixed(2)} ship · −${profits.mixed_example.fulfill_tax.toFixed(2)} tax</p>
                            <p>−${profits.mixed_example.stripe_fee.toFixed(2)} stripe+climate · −${profits.mixed_example.packing_insert.toFixed(2)} insert</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {profits.orders.length === 0 && (
                  <p className="font-mono text-sm text-muted-foreground" data-testid="profits-empty">
                    No orders yet. First sale hits different.
                  </p>
                )}
                {profits.orders.length > 0 && (
                  <Table data-testid="admin-profits-table">
                    <TableHeader>
                      <TableRow className="border-b-2 border-black">
                        <TableHead className="font-mono text-xs uppercase">Order</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Payment</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Revenue</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Sales tax²</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Print cost</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Shipping</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Printify tax</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Fees¹</TableHead>
                        <TableHead className="font-mono text-xs uppercase">Profit</TableHead>
                        <TableHead className="font-mono text-xs uppercase text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {profits.orders.map((r) => (
                        <TableRow key={r.id} data-testid={`profit-row-${r.order_number}`} className="border-b border-black/20">
                          <TableCell className="font-mono text-sm font-bold">{r.order_number}</TableCell>
                          <TableCell className="font-mono text-xs uppercase">{r.payment_status}</TableCell>
                          <TableCell className="font-mono text-sm">${r.revenue.toFixed(2)}</TableCell>
                          <TableCell className="font-mono text-sm text-black/50" title="Customer sales tax collected for the state — pass-through, not revenue">{(r.sales_tax ?? 0) > 0 ? `+$${r.sales_tax.toFixed(2)}` : "—"}</TableCell>
                          <TableCell className="font-mono text-sm">−${r.cost.toFixed(2)}</TableCell>
                          <TableCell className="font-mono text-sm">−${r.shipping.toFixed(2)}</TableCell>
                          <TableCell className="font-mono text-sm">−${r.fulfill_tax.toFixed(2)}</TableCell>
                          <TableCell className="font-mono text-sm">−${r.fees.toFixed(2)}</TableCell>
                          <TableCell className={`font-mono text-sm font-bold ${r.profit < 0 ? "text-primary" : ""}`}>
                            ${r.profit.toFixed(2)}
                            {r.cost_source === "estimate" && <span className="ml-1 text-[10px] text-black/40" title="Estimated costs — no Printify charge snapshot on this order">est</span>}
                          </TableCell>
                          <TableCell className="font-mono text-sm text-right">{r.margin}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {profits.orders.length > 0 && (
                  <p className="mt-3 font-mono text-[10px] text-black/40" data-testid="profits-footnote">
                    ¹ Stripe card processing (2.9% + 30¢) + 1% Stripe Climate. Per order also: $0.25 branded insert (folded into print cost).
                    ² Customer sales tax collected for the state via Stripe automatic tax — pass-through, never counted as revenue or profit.
                    Printify tax is the sales tax Printify charges you on fulfillment until a resale certificate is on file — rows marked "actual" use the real charged amounts.
                  </p>
                )}
              </>
            )}
          </>
        )}

        {tab === "analytics" && (
          <div data-testid="analytics-tab" className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-black/60">
                Privacy-friendly counts only — no IPs, names, or payment data. Views dedupe per visitor session; orders come from paid checkouts (never double-counted).
              </p>
              <div className="ml-auto flex gap-2">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    data-testid={`analytics-days-${d}`}
                    onClick={() => { setAnalyticsDays(d); loadAnalytics(d); }}
                    className={`border-2 border-black px-4 py-2 font-mono text-xs font-bold uppercase ${analyticsDays === d ? "bg-black text-background" : "hover:bg-accent"}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <Table data-testid="admin-analytics-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Views (m/t/d)</TableHead>
                  <TableHead>Color picks</TableHead>
                  <TableHead>Add to cart</TableHead>
                  <TableHead>Checkout starts</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Units</TableHead>
                  <TableHead>View → order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(analytics?.rows || []).map((r) => (
                  <TableRow key={r.slug} data-testid={`analytics-row-${r.slug}`}>
                    <TableCell className="font-bold">{r.name}</TableCell>
                    <TableCell className="font-mono text-xs uppercase">{r.garment_type}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.views} <span className="text-black/40">({r.devices.mobile}/{r.devices.tablet}/{r.devices.desktop})</span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.color_selects}</TableCell>
                    <TableCell className="font-mono text-xs">{r.add_to_carts}</TableCell>
                    <TableCell className="font-mono text-xs">{r.checkout_starts}</TableCell>
                    <TableCell className="font-mono text-xs font-bold">{r.orders}</TableCell>
                    <TableCell className="font-mono text-xs">{r.units_sold}</TableCell>
                    <TableCell className="font-mono text-xs">{r.view_to_order_pct == null ? "—" : `${r.view_to_order_pct}%`}</TableCell>
                  </TableRow>
                ))}
                {analytics && analytics.rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="font-mono text-sm text-black/50">No products yet.</TableCell></TableRow>
                )}
                {!analytics && (
                  <TableRow><TableCell colSpan={9} className="font-mono text-sm text-black/50">Loading…</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {tab === "reviews" && (
          <Table data-testid="admin-reviews-table">
            <TableHeader>
              <TableRow className="border-b-2 border-black">
                <TableHead className="font-mono text-xs uppercase">Product</TableHead>
                <TableHead className="font-mono text-xs uppercase">Stars</TableHead>
                <TableHead className="font-mono text-xs uppercase">Reviewer</TableHead>
                <TableHead className="font-mono text-xs uppercase">Review</TableHead>
                <TableHead className="font-mono text-xs uppercase text-right">Remove</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.length === 0 && (
                <TableRow><TableCell colSpan={5} className="font-mono text-sm text-black/50 py-8 text-center">No reviews yet — they'll appear here once customers leave them.</TableCell></TableRow>
              )}
              {reviews.map((r) => (
                <TableRow key={r.id} className="border-b border-black/10">
                  <TableCell className="font-mono text-xs">{r.product_slug}</TableCell>
                  <TableCell className="font-mono text-sm text-primary">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</TableCell>
                  <TableCell className="font-mono text-sm">{r.name}</TableCell>
                  <TableCell className="font-mono text-xs max-w-md"><span className="line-clamp-2">{r.text}</span></TableCell>
                  <TableCell className="text-right">
                    <button
                      data-testid={`delete-review-${r.id}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete this ${r.rating}-star review from ${r.name}?`)) return;
                        await api.delete(`/admin/reviews/${r.id}`).catch(() => toast.error("Delete failed"));
                        loadReviews();
                        toast.success("Review removed");
                      }}
                      className="border-2 border-black px-3 py-1 font-mono text-xs font-bold uppercase hover:bg-primary hover:text-white transition-colors"
                    >
                      Delete
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg border-2 border-black bg-background shadow-hard-lg rounded-none max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-3xl tracking-tight">
              {editing ? "Edit product" : "New product"}
            </DialogTitle>
          </DialogHeader>
          <ProductForm
            key={editing ? editing.id : "new"}
            initial={
              editing
                ? { ...editing, price: String(editing.price), sizes: editing.sizes.join(","), colors: (editing.colors || []).join(",") }
                : EMPTY_FORM
            }
            onSave={saveProduct}
            onCancel={() => setDialogOpen(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>
    </main>
  );
}
